import type { Permission } from "../auth/models/permission.model";
import { buildPermissionKey } from "../auth/models/permission.model";

function perm(resource: string, action: Permission["action"]): Permission {
  return {
    resource,
    action,
    key: buildPermissionKey(resource, action),
  };
}

/** Permissions standards ITaaS Business Core */
export const PERMISSIONS = {
  employee: {
    read: perm("employee", "read"),
    create: perm("employee", "create"),
    update: perm("employee", "update"),
    delete: perm("employee", "delete"),
  },
  department: {
    read: perm("department", "read"),
    create: perm("department", "create"),
    update: perm("department", "update"),
    delete: perm("department", "delete"),
    manage: perm("department", "manage"),
  },
  position: {
    read: perm("position", "read"),
    create: perm("position", "create"),
    update: perm("position", "update"),
    delete: perm("position", "delete"),
  },
  organization: {
    read: perm("organization", "read"),
    manage: perm("organization", "manage"),
  },
  attendance: {
    read: perm("attendance", "read"),
    create: perm("attendance", "create"),
    update: perm("attendance", "update"),
    manage: perm("attendance", "manage"),
    analytics: perm("attendance", "analytics"),
  },
  leave: {
    read: perm("leave", "read"),
    create: perm("leave", "create"),
    update: perm("leave", "update"),
    approve: perm("leave", "approve"),
    manage: perm("leave", "manage"),
  },
  schedule: {
    read: perm("schedule", "read"),
    create: perm("schedule", "create"),
    update: perm("schedule", "update"),
    manage: perm("schedule", "manage"),
  },
  performance: {
    read: perm("performance", "read"),
    create: perm("performance", "create"),
    update: perm("performance", "update"),
    review: perm("performance", "review"),
    manage: perm("performance", "manage"),
  },
  compensation: {
    read: perm("compensation", "read"),
    manage: perm("compensation", "manage"),
  },
  bonus: {
    read: perm("bonus", "read"),
    manage: perm("bonus", "manage"),
  },
  payroll: {
    read: perm("payroll", "read"),
    run: perm("payroll", "run"),
    adjust: perm("payroll", "adjust"),
  },
  training: {
    read: perm("training", "read"),
    manage: perm("training", "manage"),
  },
  course: {
    read: perm("course", "read"),
    manage: perm("course", "manage"),
  },
  certification: {
    read: perm("certification", "read"),
    manage: perm("certification", "manage"),
  },
  career: {
    read: perm("career", "read"),
    manage: perm("career", "manage"),
  },
  promotion: {
    read: perm("promotion", "read"),
    manage: perm("promotion", "manage"),
  },
  succession: {
    read: perm("succession", "read"),
    manage: perm("succession", "manage"),
  },
  recruitment: {
    read: perm("recruitment", "read"),
    manage: perm("recruitment", "manage"),
  },
  candidate: {
    read: perm("candidate", "read"),
    manage: perm("candidate", "manage"),
  },
  hiring: {
    manage: perm("hiring", "manage"),
  },
  crm: {
    read: perm("crm", "read"),
    manage: perm("crm", "manage"),
  },
  ticket: {
    read: perm("ticket", "read"),
    create: perm("ticket", "create"),
    update: perm("ticket", "update"),
    assign: perm("ticket", "assign"),
    close: perm("ticket", "close"),
    manage: perm("ticket", "manage"),
  },
  incident: {
    read: perm("incident", "read"),
    create: perm("incident", "create"),
    resolve: perm("incident", "resolve"),
  },
  sla: {
    read: perm("sla", "read"),
    manage: perm("sla", "manage"),
  },
  problem: {
    read: perm("problem", "read"),
    create: perm("problem", "create"),
    update: perm("problem", "update"),
    close: perm("problem", "close"),
  },
  change: {
    read: perm("change", "read"),
    create: perm("change", "create"),
    approve: perm("change", "approve"),
  },
  project: {
    read: perm("project", "read"),
    create: perm("project", "create"),
    manage: perm("project", "manage"),
  },
  knowledge: {
    read: perm("knowledge", "read"),
    create: perm("knowledge", "create"),
    publish: perm("knowledge", "publish"),
  },
  customer: {
    read: perm("customer", "read"),
    create: perm("customer", "create"),
  },
  contract: {
    read: perm("contract", "read"),
    create: perm("contract", "create"),
    manage: perm("contract", "manage"),
  },
  billing: {
    read: perm("billing", "read"),
    manage: perm("billing", "manage"),
  },
  service: {
    read: perm("service", "read"),
    manage: perm("service", "manage"),
  },
  portal: {
    ticketRead: {
      resource: "portal",
      action: "read",
      key: "portal.ticket.read",
    },
    assetRead: {
      resource: "portal",
      action: "read",
      key: "portal.asset.read",
    },
    contractRead: {
      resource: "portal",
      action: "read",
      key: "portal.contract.read",
    },
    invoiceRead: {
      resource: "portal",
      action: "read",
      key: "portal.invoice.read",
    },
    userRead: {
      resource: "portal",
      action: "read",
      key: "portal.user.read",
    },
    recruitmentRead: {
      resource: "portal",
      action: "read",
      key: "portal.recruitment.read",
    },
    recruitmentManage: {
      resource: "portal",
      action: "manage",
      key: "portal.recruitment.manage",
    },
  },
  request: {
    read: perm("request", "read"),
    create: perm("request", "create"),
    approve: perm("request", "approve"),
    complete: perm("request", "complete"),
  },
  approval: {
    read: perm("approval", "read"),
    manage: perm("approval", "manage"),
  },
  actionValidation: {
    read: perm("action_validation", "read"),
    manage: perm("action_validation", "manage"),
    approve: perm("action_validation", "approve"),
    submit: perm("action_validation", "submit"),
  },
  automation: {
    read: perm("automation", "read"),
    manage: perm("automation", "manage"),
  },
  asset: {
    read: perm("asset", "read"),
    create: perm("asset", "create"),
    update: perm("asset", "update"),
    assign: perm("asset", "assign"),
    maintain: perm("asset", "maintain"),
    retire: perm("asset", "retire"),
    manage: perm("asset", "manage"),
  },
  inventory: {
    read: perm("inventory", "read"),
    create: perm("inventory", "create"),
    update: perm("inventory", "update"),
    manage: perm("inventory", "manage"),
  },
  security: {
    read: perm("security", "read"),
    manage: perm("security", "manage"),
  },
  user: {
    read: perm("user", "read"),
    create: perm("user", "create"),
    update: perm("user", "update"),
    delete: perm("user", "delete"),
    manage: perm("user", "manage"),
  },
  role: {
    read: perm("role", "read"),
    manage: perm("role", "manage"),
  },
  invitation: {
    read: perm("invitation", "read"),
    create: perm("invitation", "create"),
    cancel: perm("invitation", "cancel"),
  },
  settings: {
    manage: perm("settings", "manage"),
  },
  tenant: {
    manage: perm("tenant", "manage"),
  },
  subscription: {
    manage: perm("subscription", "manage"),
  },
  platform: {
    manage: perm("platform", "manage"),
  },
  support: {
    manage: perm("support", "manage"),
  },
} as const;

export type PermissionKey =
  | (typeof PERMISSIONS)[keyof typeof PERMISSIONS][keyof (typeof PERMISSIONS)[keyof typeof PERMISSIONS]]["key"];

export const ALL_PERMISSION_KEYS: string[] = Object.values(PERMISSIONS).flatMap(
  (group) => Object.values(group).map((p) => p.key)
);

export const PLATFORM_PERMISSION_KEYS = [
  PERMISSIONS.tenant.manage.key,
  PERMISSIONS.subscription.manage.key,
  PERMISSIONS.billing.manage.key,
  PERMISSIONS.platform.manage.key,
  PERMISSIONS.support.manage.key,
] as const;
